import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { 
  FileSignature, Shield, CheckCircle, Clock, AlertTriangle,
  User, Calendar, Lock, ExternalLink, Info
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { fmtDateTimeWeek } from '../../lib/dateFormat';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Signature type configuration
const SIGNATURE_TYPES = {
  internal: {
    label: i18n.t('auto.lbl.internalDigitalSignature'),
    description: i18n.t('auto.lbl.cryptographicHashWithTimestampAndUser'),
    icon: Lock,
    color: 'bg-blue-100 text-blue-800',
  },
  podpis_zaufany: {
    label: i18n.t('auto.lbl.podpisZaufany'),
    description: 'Polish national trusted signature (pz.gov.pl)',
    icon: Shield,
    color: 'bg-green-100 text-green-800',
  },
  qualified: {
    label: i18n.t('auto.lbl.qualifiedElectronicSignature'),
    description: i18n.t('auto.lbl.euCompliantQualifiedElectronicSignature'),
    icon: Shield,
    color: 'bg-purple-100 text-purple-800',
  },
};

// Sign Document Dialog
const SignDocumentDialog = ({ 
  open, 
  onOpenChange, 
  documentType, 
  documentId, 
  documentTitle,
  token,
  onSuccess 
}) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [comments, setComments] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [signatureResult, setSignatureResult] = useState(null);

  const handleSign = async () => {
    if (!password) {
      toast.error(t('auto.iso.pleaseEnterYourPassword'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/signatures/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          document_type: documentType,
          document_id: documentId,
          password,
          comments,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Signing failed');
      }

      const result = await response.json();
      setSignatureResult(result);
      toast.success(t('auto.iso.documentSignedSuccessfully'));
      if (onSuccess) onSuccess(result);
    } catch (error) {
      console.error('Signing error:', error);
      toast.error(error.message || 'Failed to sign document');
    } finally {
      setIsLoading(false);
    }
  };

  const resetDialog = () => {
    setPassword('');
    setComments('');
    setSignatureResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open) resetDialog();
      onOpenChange(open);
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-blue-600" />
            {t('auto.iso.electronicSignature')}
          </DialogTitle>
          <DialogDescription>
            Sign document: {documentTitle}
          </DialogDescription>
        </DialogHeader>

        {signatureResult ? (
          // Success view
          <div className="py-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{t('auto.iso.documentSigned')}</h3>
              <p className="text-sm text-gray-500">
                Signed at {fmtDateTimeWeek(signatureResult.signed_at)}
              </p>
            </div>
            
            <Card className="bg-gray-50 text-left">
              <CardContent className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('auto.iso.signatureId')}</span>
                  <span className="font-mono text-xs">{signatureResult.signature_id.substring(0, 12)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('auto.iso.type2')}</span>
                  <Badge variant="outline">{t('auto.iso.internal')}</Badge>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-gray-500">{t('auto.iso.hash')}</span>
                  <span className="font-mono text-xs break-all max-w-[200px]">
                    {signatureResult.signature_hash.substring(0, 24)}...
                  </span>
                </div>
              </CardContent>
            </Card>
            
            <Button onClick={() => onOpenChange(false)} className="w-full" data-testid="btn-close">
              {t('auto.iso.close')}
            </Button>
          </div>
        ) : (
          // Sign form view
          <div className="space-y-4 py-4">
            {/* Signature Type Info */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">{t('auto.iso.internalDigitalSignature')}</p>
                  <p className="text-sm text-blue-700 mt-1">
                    {t('auto.iso.yourSignatureWillBeCreatedUsing')}
                  </p>
                  <ul className="text-xs text-blue-600 mt-2 space-y-1">
                    <li>{t('auto.iso.yourUserIdentity')}</li>
                    <li>{t('auto.iso.documentContentHash')}</li>
                    <li>{t('auto.iso.timestampUtc')}</li>
                    <li>{t('auto.iso.passwordVerification')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Podpis Zaufany notice */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-700">
                  <p className="font-medium">{t('auto.iso.podpisZaufanyIntegration')}</p>
                  <p className="mt-1">
                    Polish national electronic signature (pz.gov.pl) integration is prepared but requires 
                    formal government registration. Contact your administrator to enable.
                  </p>
                </div>
              </div>
            </div>

            {/* Password input */}
            <div>
              <label className="text-sm font-medium">{t('auto.iso.enterYourPasswordToSign')}</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="password"
                  placeholder={t('auto.iso.yourAccountPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="signature-password-input"
                />
              </div>
            </div>

            {/* Comments */}
            <div>
              <label className="text-sm font-medium">{t('auto.iso.commentsOptional')}</label>
              <textarea
                className="w-full min-h-[60px] p-2 border rounded-md text-sm mt-1"
                placeholder={t('auto.iso.addAnyCommentsAboutThisSignature')}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <p>
                By signing, you confirm that you have reviewed this document and authorize its contents.
                This action will be permanently recorded.
              </p>
            </div>
          </div>
        )}

        {!signatureResult && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">
              {t('auto.iso.cancel')}
            </Button>
            <Button 
              onClick={handleSign} 
              disabled={isLoading || !password}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-signature-btn"
            >
              {isLoading ? (
                'Signing...'
              ) : (
                <>
                  <FileSignature className="h-4 w-4 mr-2" />
                  {t('auto.iso.signDocument')}
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Signature List Component
const SignatureList = ({ signatures = [], showVerify = true }) => {
  const { t } = useTranslation();
  if (!signatures || signatures.length === 0) {
    return (
      <div className="text-sm text-gray-500 text-center py-4">
        <FileSignature className="h-8 w-8 mx-auto mb-2 text-gray-300" />
        <p>{t('auto.iso.noSignatures')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {signatures.map((sig, index) => {
        const typeConfig = SIGNATURE_TYPES[sig.signature_type] || SIGNATURE_TYPES.internal;
        const TypeIcon = typeConfig.icon;
        
        return (
          <Card key={sig.id || index} className="overflow-hidden">
            <CardContent className="p-3">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-full ${typeConfig.color.replace('text-', 'bg-').replace('-800', '-100')}`}>
                    <TypeIcon className={`h-4 w-4 ${typeConfig.color.split(' ')[1]}`} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{sig.signer_name}</p>
                    <p className="text-xs text-gray-500">{sig.signer_email}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                      <Calendar className="h-3 w-3" />
                      {fmtDateTimeWeek(sig.signed_at)}
                    </div>
                    {sig.comments && (
                      <p className="text-xs text-gray-500 mt-1 italic">&ldquo;{sig.comments}&rdquo;</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={typeConfig.color}>
                    {typeConfig.label.split(' ')[0]}
                  </Badge>
                  {sig.verified && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      {t('auto.iso.verified')}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Signature hash preview */}
              <div className="mt-2 pt-2 border-t">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">{t('auto.iso.hash')}</span>
                  <span className="font-mono text-gray-500">
                    {sig.signature_hash?.substring(0, 32)}...
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Verify Signature Dialog
const VerifySignatureDialog = ({ open, onOpenChange, signatureId, token }) => {
  const { t } = useTranslation();
  const [verificationResult, setVerificationResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const verifySignature = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/signatures/verify/${signatureId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Verification failed');
      }

      const result = await response.json();
      setVerificationResult(result);
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(t('auto.iso.failedToVerifySignature'));
    } finally {
      setIsLoading(false);
    }
  }, [signatureId, token]);

  useEffect(() => {
    if (open && signatureId) {
      verifySignature();
    }
  }, [open, signatureId, verifySignature]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t('auto.iso.signatureVerification')}
          </DialogTitle>
          <DialogDescription className="sr-only">{t('auto.iso.dialog')}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center">
            <Clock className="h-8 w-8 mx-auto animate-spin text-blue-500" />
            <p className="mt-2 text-gray-500">{t('auto.iso.verifyingSignature')}</p>
          </div>
        ) : verificationResult ? (
          <div className="py-4 space-y-4">
            <div className={`p-4 rounded-lg text-center ${
              verificationResult.is_valid ? 'bg-green-50' : 'bg-red-50'
            }`}>
              {verificationResult.is_valid ? (
                <CheckCircle className="h-12 w-12 mx-auto text-green-600" />
              ) : (
                <AlertTriangle className="h-12 w-12 mx-auto text-red-600" />
              )}
              <p className={`mt-2 font-semibold ${
                verificationResult.is_valid ? 'text-green-800' : 'text-red-800'
              }`}>
                {verificationResult.is_valid ? t('auto.iso.signatureValid') : t('auto.iso.signatureInvalid')}
              </p>
            </div>

            <Card>
              <CardContent className="p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('auto.iso.signer')}</span>
                  <span>{verificationResult.signer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('auto.iso.email')}</span>
                  <span>{verificationResult.signer_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('auto.iso.signedAt')}</span>
                  <span>{fmtDateTimeWeek(verificationResult.signed_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('auto.iso.type2')}</span>
                  <Badge variant="outline">{verificationResult.signature_type}</Badge>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-xs text-gray-400">{verificationResult.verification_note}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} data-testid="dialog-close-btn">{t('auto.iso.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Electronic Signature Panel Component
const ElectronicSignaturePanel = ({ 
  documentType, 
  documentId, 
  documentTitle,
  signatures = [],
  token,
  onSignatureAdded,
  canSign = true
}) => {
  const { t } = useTranslation();
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [selectedSignatureId, setSelectedSignatureId] = useState(null);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSignature className="h-4 w-4" />
            {t('auto.iso.electronicSignatures')}
          </CardTitle>
          {canSign && (
            <Button 
              size="sm" 
              onClick={() => setShowSignDialog(true)}
              data-testid="sign-document-btn"
            >
              <FileSignature className="h-4 w-4 mr-1" />
              {t('auto.iso.sign')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <SignatureList signatures={signatures} />
      </CardContent>

      {/* Sign Dialog */}
      <SignDocumentDialog
        open={showSignDialog}
        onOpenChange={setShowSignDialog}
        documentType={documentType}
        documentId={documentId}
        documentTitle={documentTitle}
        token={token}
        onSuccess={onSignatureAdded}
      />

      {/* Verify Dialog */}
      <VerifySignatureDialog
        open={showVerifyDialog}
        onOpenChange={setShowVerifyDialog}
        signatureId={selectedSignatureId}
        token={token}
      />
    </Card>
  );
};

export default ElectronicSignaturePanel;
export { SignDocumentDialog, SignatureList, VerifySignatureDialog };
